import * as React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Edit2, Trash2, Plus } from "lucide-react";
import { motion } from "framer-motion";
import {
  useGetSingleFeeQuery,
  useDeleteFeeMutation,
  useSaveFeeMutation,
  useUpdateFeeMutation,
} from "../store/feeApi";
import { useSelector } from "react-redux";
import { Fee, RootState } from "@/types";
import { Link, useParams } from "react-router-dom";
import ReceiptExportButton from "./Admin/ReceiptExportButton";
import { Dialog, DialogContent, DialogTrigger } from "./ui/dialog";
// import FeeForm from "./FeeForm";
import { useGetSingleStudentQuery } from "@/store/studentApi";
import { toast } from "sonner";
import FeeForm from "./AddFees";
import { CustomAlertDialog } from "./AlertDialog";

const FeeHistory = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const [hoveredRow, setHoveredRow] = React.useState<number | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedFee, setSelectedFee] = React.useState(null);

  const { id } = useParams();
  const { data: fee } = useGetSingleFeeQuery(id);
  const { data: student } = useGetSingleStudentQuery(id);

  const [deleteFee] = useDeleteFeeMutation();
  const [saveFee] = useSaveFeeMutation();
  const [updateFee] = useUpdateFeeMutation();

  const totalFee = Number(fee?.TotalFee) || 0;
  const paidFee = Number(fee?.PaidFee) || 0;
  const pendingFee = (totalFee - paidFee).toLocaleString();

  // Sort payments chronologically (oldest first) to compute cumulative paid fees
  const chronologicalPayments = React.useMemo(() => {
    if (!fee?.fee) return [];
    return [...fee.fee].sort((a, b) => {
      const getReceiptValue = (receiptNo: string): number => {
        if (!receiptNo) return 0;
        const numStr = receiptNo.replace(/EFRPNO/i, "");
        const parsed = parseInt(numStr, 10);
        return isNaN(parsed) ? 0 : parsed;
      };
      const valA = getReceiptValue(a.receiptNo);
      const valB = getReceiptValue(b.receiptNo);
      if (valA !== valB) return valA - valB;
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });
  }, [fee?.fee]);

  // Create a map of payment ID to cumulative paid amount up to and including that payment
  const cumulativePaidMap = React.useMemo(() => {
    const map = new Map<string, number>();
    let runningTotal = 0;
    chronologicalPayments.forEach((payment) => {
      runningTotal += Number(payment.paid) || 0;
      map.set(payment._id, runningTotal);
    });
    return map;
  }, [chronologicalPayments]);

  // Reset selected fee when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedFee(null);
    }
  }, [isOpen]);

  const handleSubmit = async (data: Fee) => {
    if (selectedFee) {
      await updateFee(data);
    } else {
      await saveFee(data);
    }
  };

  const handleDelete = async (paymentId: string) => {
    try {
      await deleteFee({ id: paymentId, Token: user?.token });
      toast.success("Payment deleted successfully");
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast.error(error || "Failed to delete payment");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <Link to={`/${student?.username}`}>
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">
                {student?.profile ? (
                  <img
                    className="size-12 rounded-full object-cover"
                    src={student?.profile}
                    alt={fee?.fee[0]?.name}
                  />
                ) : (
                  fee?.fee[0]?.name?.charAt(0)
                )}
              </div>
            </Link>
          </div>
          <div>
            <h2 className="text-2xl text-foreground font-bold">Fee History</h2>
            <p className="text-muted-foreground">
              <span className="font-semibold">Student:</span>{" "}
              {fee?.fee[0]?.name}
            </p>
          </div>
        </div>

        {user?.role.toLowerCase() === "hr" && (
          <div className="flex gap-4">
            <button
              onClick={() => setIsOpen(true)}
              className="px-4 py-2 bg-red-500 text-white rounded-lg flex items-center gap-2 hover:bg-red-600 transition-colors"
            >
              <Plus size={20} />
              Add Fee
            </button>
          </div>
        )}
      </div>

      {/* Fee Form Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger />
        <DialogContent className="max-w-2xl -bottom-32 overflow-y-auto">
          <FeeForm
            initialData={selectedFee || fee}
            isUpdate={!!selectedFee}
            onSubmit={handleSubmit}
            setIsOpen={setIsOpen}
          />
          .
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-600">Total Fee</p>
            <h3 className="text-2xl font-bold">₹{totalFee}</h3>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-600">Paid Fee</p>
            <h3 className="text-2xl font-bold text-red-500">₹{paidFee}</h3>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-600">Pending Fee</p>
            <h3 className="text-2xl font-bold text-red-500">₹{pendingFee}</h3>
          </CardContent>
        </Card>
      </div>

      {/* Payment History Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            <h1>Payment History</h1>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="py-4 px-6 text-left">Receipt Number</th>
                  <th className="py-4 px-6 text-left">Date</th>
                  <th className="py-4 px-6 text-left">Batch</th>
                  <th className="py-4 px-6 text-left">Course</th>
                  <th className="py-4 px-6 text-right">Paid Fee</th>
                  <th className="py-4 px-6 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {fee?.fee
                  ?.slice() // create a copy so we don't mutate the original
                  .sort((a, b) => {
                    const getReceiptValue = (receiptNo: string): number => {
                      if (!receiptNo) return 0;
                      const numStr = receiptNo.replace(/EFRPNO/i, "");
                      const parsed = parseInt(numStr, 10);
                      return isNaN(parsed) ? 0 : parsed;
                    };
                    const valA = getReceiptValue(a.receiptNo);
                    const valB = getReceiptValue(b.receiptNo);
                    if (valA !== valB) return valB - valA;
                    const aTime = a.createdAt
                      ? new Date(a.createdAt).getTime()
                      : 0;
                    const bTime = b.createdAt
                      ? new Date(b.createdAt).getTime()
                      : 0;
                    return bTime - aTime;
                  }) // sort by date (newest first)
                  .map((payment, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onMouseEnter={() => setHoveredRow(index)}
                      onMouseLeave={() => setHoveredRow(null)}
                      className={`border-b hover:bg-muted transition-colors ${
                        hoveredRow === index ? "bg-gray-50" : ""
                      }`}
                    >
                      <td className="py-4 px-6">{payment.receiptNo}</td>
                      <td className="py-4 px-6">
                        {new Date(payment.date).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-6">{payment.bCode}</td>
                      <td className="py-4 px-6">{payment.course}</td>
                      <td className="py-4 px-6 text-right">
                        ₹{payment.paid.toLocaleString()}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-center items-center gap-4">
                          <ReceiptExportButton
                            payment={payment}
                            totalFee={totalFee}
                            paidFee={payment?.paid}
                            pendingFee={
                              totalFee -
                              (cumulativePaidMap.get(payment._id) ||
                                payment.paid)
                            }
                          />
                          {user?.role.toLowerCase() === "hr" && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedFee({ ...fee, fee: [payment] });
                                  setIsOpen(true);
                                }}
                                className="text-gray-500 hover:text-gray-600"
                              >
                                <Edit2 size={20} />
                              </button>

                              <CustomAlertDialog
                                description="Are you sure you want to delete this payment?"
                                title="Delete the Receipt"
                                onConfirm={() => handleDelete(payment._id)}
                                triggerButton={
                                  <button className="text-red-500 hover:text-red-600">
                                    <Trash2 size={20} />
                                  </button>
                                }
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeeHistory;
